import { z } from 'zod';
import { ReactElement, useMemo, useContext, useState, useCallback } from 'react';
import { DrawerForm, ErrorResponse, SuccessNotificationContext, handleFieldErrors } from '@dmc/ui';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import {
  PersonnelSafetySettingsModel,
  PersonnelSafetySettingsRequestModel,
  usePutPersonnelSafetySettingsMutation,
} from '@casops/store/apis/settingsApi';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppDispatch } from '@casops/store/hooks';
import { closeSidePanel } from '@casops/store/slices/globalSlice';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ApiBaseQueryError } from '@casops/store/api';
import { VdsButton } from '@dmc/ui/vds';
import ErrorMessage from '@cas/components/state-management/error-message/ErrorMessage';
import { useForm } from 'react-hook-form';
import CasFormCheckbox from '@cas/components/inputs/cas-form-checkbox/CasFormCheckbox';
import styles from './SafetySettingsForm.module.scss';
import { SafetyNotifications } from '../safety-notifications/SafetyNotifications';
import { SafetyManagers } from '../safety-managers/SafetyManagers';
import { ExternalEmailsWrapper } from '../external-emails-wrapper/ExternalEmailsWrapper';

interface FormInputModel {
  trackDuress: boolean;
  trackManDown: boolean;
  externalEmailAddresses: string;
}

const emailSeparator = ';';

const defaultSettings: FormInputModel = {
  trackDuress: true,
  trackManDown: true,
  externalEmailAddresses: '',
};

interface SafetySettingsFormProps {
  settings?: PersonnelSafetySettingsModel;
}

export const SafetySettingsForm = ({ settings }: SafetySettingsFormProps): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const [putMutationTrigger] = usePutPersonnelSafetySettingsMutation();

  // Merge default values with the API response.
  const initialValues = useMemo<FormInputModel>(() => {
    if (settings !== undefined) {
      return {
        trackDuress: settings.trackDuress,
        trackManDown: settings.trackManDown,
        externalEmailAddresses: settings.externalEmailAddresses.join(emailSeparator),
      };
    }

    return defaultSettings;
  }, [settings]);

  const schema = useMemo(() => {
    return z.object({
      trackDuress: z.boolean(),
      trackManDown: z.boolean(),
      externalEmailAddresses: z.string(),
    });
  }, []);

  type SchemaOutput = z.infer<typeof schema>;

  const {
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting, dirtyFields, isValid },
  } = useForm<FormInputModel, undefined, SchemaOutput>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  // Don't use the isDirty flag with ReactHookForm - see https://github.com/react-hook-form/react-hook-form/issues/3213.
  const isAnyFieldDirty = Object.keys(dirtyFields).length > 0;
  const canSubmit = !isSubmitting && isValid && isAnyFieldDirty;

  const onSubmitInternal = useCallback(
    async (data: SchemaOutput) => {
      try {
        setErrorMessage(undefined);

        const payload: PersonnelSafetySettingsRequestModel = {
          trackDuress: data.trackDuress,
          trackManDown: data.trackManDown,
          externalEmailAddresses: data.externalEmailAddresses
            .split(emailSeparator)
            .map(e => e.trim())
            .filter(e => e.length > 0),
        };

        await putMutationTrigger(payload).unwrap();

        // If we got here, it's successful because the methods above would have thrown
        showSuccessMessage(t(`SettingsSavedSuccessful`));

        dispatch(closeSidePanel());
      } catch (e: ApiBaseQueryError) {
        const errorResponse = new ErrorResponse(e);
        const validationError = errorResponse.getValidationError();

        if (validationError) {
          handleFieldErrors<FormInputModel>({
            serverErrorFields: { externalEmailAddresses: 'externalEmailAddresses' },
            validationError,
            setError,
          });
        }

        setErrorMessage(t('Failed!') + ' ' + errorResponse.getTitle());
      }
    },
    [t, dispatch, setError, setErrorMessage, putMutationTrigger, showSuccessMessage],
  );

  const onSubmit = useCallback((): void => {
    handleSubmit(onSubmitInternal)();
  }, [handleSubmit, onSubmitInternal]);

  return (
    <DrawerForm
      footer={
        <VdsButton
          variant="primary"
          color="secondary-blue"
          isFullWidth
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          {t('SaveChanges')}
        </VdsButton>
      }
    >
      <>
        {errorMessage !== undefined && <ErrorMessage message={errorMessage} />}

        <div className={styles.subTitle}>{t('TrackedAlerts')}</div>
        <div className={styles.checkboxGroup}>
          <CasFormCheckbox
            control={control}
            name="trackDuress"
            label={t('Model.Name.SafetyDuress')}
          />
          <CasFormCheckbox
            control={control}
            name="trackManDown"
            label={t('Model.Name.SafetyManDown')}
          />
        </div>
        <div className={`${styles.subTitle} ${styles.extraPadding}`}>
          {t('NotificationRecipients')}
        </div>
        <SafetyNotifications />
        <SafetyManagers safetyManagers={settings?.safetyManagers ?? []} />
        <ExternalEmailsWrapper>
          <CasFormInput
            control={control}
            name="externalEmailAddresses"
            label=""
            element="textarea"
            helperText={t('Field.ExternalTeamMembers.HelperText')}
          />
        </ExternalEmailsWrapper>
      </>
    </DrawerForm>
  );
};

export default SafetySettingsForm;
